import { useMemo, useState, useEffect, useRef } from "react";
import { format, startOfWeek, parse, getDay, isSameDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useUpdateAppointment, type AppointmentWithDetails, type Veterinarian } from "@/hooks/useAppointments";
import { Calendar, Views, View } from 'react-big-calendar';
import { dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './calendar-styles.css';
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAppointmentStatuses } from "@/hooks/useAppointmentStatuses";
import type { CalendarEvent, CalendarPet, ResourceHeaderProps } from "@/components/calendar/views/CalendarEventTypes";
import { ResourceHeader } from "@/components/calendar/views/ResourceHeader";
import { DefaultEvent } from "@/components/calendar/views/DefaultEvent";
import { MonthEvent } from "@/components/calendar/views/MonthEvent";
import { WeekEvent } from "@/components/calendar/views/WeekEvent";
import { DayEvent } from "@/components/calendar/views/DayEvent";
import { getEventTypeBackgroundColor, getEventTypeClasses, getEventTypeBorderColor, hexToRgba } from "@/components/calendar/views/calendarEventUtils";
import { useTranslation } from "react-i18next";
import { startCancelableSmoothScroll } from "@/lib/cancelableSmoothScroll";
import { buildCalendarEvents } from "@/components/calendar/views/buildCalendarEvents";

const DnDCalendar = withDragAndDrop(Calendar);

const TIMEZONE = 'America/Sao_Paulo';

interface CalendarViewProps {
  viewMode: "day" | "week" | "month" | "agenda";
  setViewMode: (viewMode: "day" | "week" | "month" | "agenda") => void;
  clinicId: string;
  appointments: AppointmentWithDetails[];
  professionals: Veterinarian[];
  showAppointmentItemsOnCards?: boolean;
  currentDate: Date;
  onNavigate: (date: Date) => void;
  onAppointmentClick?: (appointmentId: string) => void;
  onCreateAppointment?: (date: Date, startTime?: string, endTime?: string, vetUserId?: string | null) => void;
}

// Localizer for date-fns
const localizer = dateFnsLocalizer({
  format: (date, formatStr) => format(date, formatStr, { locale: ptBR }),
  parse: (value, formatString) => parse(value, formatString, new Date(), { locale: ptBR }),
  startOfWeek: (date) => startOfWeek(date, { locale: ptBR }),
  getDay: getDay,
  locales: { 'pt-BR': ptBR },
});

// Custom Event Component
export function CalendarView({
  viewMode,
  setViewMode,
  appointments,
  professionals,
  showAppointmentItemsOnCards = false,
  currentDate,
  onNavigate,
  onAppointmentClick,
  onCreateAppointment
}: CalendarViewProps) {
  const [isShowMoreOpen, setIsShowMoreOpen] = useState(false);
  const updateAppointment = useUpdateAppointment();
  const calendarRef = useRef<HTMLDivElement>(null);
  const cancelScrollRef = useRef<(() => void) | null>(null);
  const { data: appointmentStatuses = [] } = useAppointmentStatuses("appointment");
  const { data: groomingStatuses = [] } = useAppointmentStatuses("grooming");
  const { t } = useTranslation("calendar");
  const allStatuses = useMemo(() => [...appointmentStatuses, ...groomingStatuses], [appointmentStatuses, groomingStatuses]);

  const calendarMin = useMemo(() => {
    const defaultHour = 7;
    if (!appointments.length) return new Date(2000, 0, 1, defaultHour, 0, 0);
    let minHour = defaultHour;
    for (const appt of appointments) {
      if (!appt.starts_at) continue;
      const d = new Date(appt.starts_at);
      const h = d.getHours();
      if (h < minHour) minHour = h;
    }
    return new Date(2000, 0, 1, minHour, 0, 0);
  }, [appointments]);

  const professionalsById = useMemo(() => {
    return new Map<string, Veterinarian>((professionals ?? []).map((p) => [p.id, p]));
  }, [professionals]);

  // Convert appointments to react-big-calendar events format with timezone
  const events = useMemo<CalendarEvent[]>(
    () => buildCalendarEvents(appointments, { professionalsById, t }),
    [appointments, professionalsById, t],
  );

  const dayResources = useMemo(() => {
    if (viewMode !== "day") return undefined;

    const currentZoned = toZonedTime(currentDate, TIMEZONE);
    const dayEvents = events.filter((event) => {
      const eventStartZoned = toZonedTime(new Date(event.start), TIMEZONE);
      return isSameDay(eventStartZoned, currentZoned);
    });

    const resourcesById = new Map<string, { id: string; full_name: string; role: string | null }>();

    for (const ev of dayEvents) {
      const rid = ev.resourceId ?? "unassigned";
      if (rid === "unassigned") continue;
      if (!resourcesById.has(rid)) {
        const prof = professionalsById.get(rid);
        resourcesById.set(rid, {
          id: rid,
          full_name: prof?.full_name ?? ev.vet?.full_name ?? t("professionalLabel"),
          role: prof?.role ?? null,
        });
      }
    }

    const resources = Array.from(resourcesById.values()).sort((a, b) =>
      a.full_name.localeCompare(b.full_name, "pt-BR", { sensitivity: "base" }),
    );

    const hasUnassigned = dayEvents.some((ev) => (ev.resourceId ?? "unassigned") === "unassigned");

    if (hasUnassigned) {
      resources.push({ id: "unassigned", full_name: t("unassigned"), role: null });
    }

    if (resources.length === 0) {
      resources.push({ id: "unassigned", full_name: "", role: null });
    }

    return resources;
  }, [currentDate, events, professionalsById, viewMode, t]);

  // Map viewMode to react-big-calendar views
  const getView = (): View => {
    switch (viewMode) {
      case "day":
        return Views.DAY;
      case "week":
        return Views.WEEK;
      case "month":
        return Views.MONTH;
      case "agenda":
        return Views.AGENDA;
      default:
        return Views.WEEK;
    }
  };

  // Handle event drop (rescheduling) with timezone and optimistic update
  const handleEventDrop = ({
    event,
    start,
    end,
    resourceId,
  }: {
    event: CalendarEvent;
    start: Date;
    end: Date;
    resourceId?: string;
  }) => {
    const zonedStart = fromZonedTime(start, TIMEZONE);
    const zonedEnd = fromZonedTime(end, TIMEZONE);

    const nextVetUserId =
      typeof resourceId === "string"
        ? resourceId === "unassigned"
          ? null
          : resourceId
        : undefined;

    toast.promise(
      updateAppointment.mutateAsync({
        id: event.id,
        input: {
          starts_at: zonedStart.toISOString(),
          ends_at: zonedEnd.toISOString(),
          ...(nextVetUserId !== undefined ? { vet_user_id: nextVetUserId } : {}),
        },
      }),
      {
        loading: t("updatingAppointment"),
        success: t("appointmentUpdated"),
        error: t("updateAppointmentError"),
      }
    );
  };

  // Handle event resize (duration change) with timezone and optimistic update
  const handleEventResize = ({ event, start, end }: { event: CalendarEvent; start: Date; end: Date }) => {
    const zonedStart = fromZonedTime(start, TIMEZONE);
    const zonedEnd = fromZonedTime(end, TIMEZONE);

    toast.promise(
      updateAppointment.mutateAsync({
        id: event.id,
        input: {
          starts_at: zonedStart.toISOString(),
          ends_at: zonedEnd.toISOString(),
        },
      }),
      {
        loading: t("updatingDuration"),
        success: t("durationUpdated"),
        error: t("updateDurationError"),
      }
    );
  };

  // Handle slot selection (create appointment) with timezone
  const handleSelectSlot = ({ start, end, resourceId }: { start: Date; end: Date; resourceId?: string | number }) => {
    if (isShowMoreOpen) {
      setIsShowMoreOpen(false);
      return;
    }

    const zonedStart = toZonedTime(start, TIMEZONE);
    const zonedEnd = toZonedTime(end, TIMEZONE);
    const startTime = format(zonedStart, "HH:mm");
    const endTime = format(zonedEnd, "HH:mm");
    const vetUserId = resourceId === "unassigned" || resourceId === undefined ? null : String(resourceId);
    onCreateAppointment?.(zonedStart, startTime, endTime, vetUserId);
  };

  // Handle event selection (view details)
  const handleSelectEvent = (event: CalendarEvent) => {
    onAppointmentClick?.(event.id);
  };

  // Event style getter for appointment type colors
  const eventStyleGetter = (event: CalendarEvent) => {
    const baseClasses = [
      // Ensure consistent layout and better focus ring.
      "group relative overflow-hidden",
      "focus-visible:outline-none",
    ];

    const behavior = event.clinic_appointment_type?.behavior ?? event.type;
    const customColor = event.clinic_appointment_type?.color ?? undefined;
    const borderColor = customColor ?? getEventTypeBorderColor(behavior);
    return {
      className: [...baseClasses, getEventTypeClasses(behavior)].join(" "),
      style: {
        "--event-type-bg": getEventTypeBackgroundColor(behavior),
        borderLeftWidth: "2px",
        borderLeftStyle: "solid",
        borderLeftColor: borderColor,
        ...(customColor ? { background: `linear-gradient(${hexToRgba(customColor, 0.08)}, ${hexToRgba(customColor, 0.08)}), #ffffff` } : {}),
      } as React.CSSProperties,
    };
  };

  // Custom components for different views with statuses
  const components = useMemo(() => ({
    resourceHeader: (props: unknown) => <ResourceHeader {...(props as ResourceHeaderProps)} />,
    // Default event component (fallback)
    event: (props: unknown) => {
      const { event } = props as { event: CalendarEvent };
      return <DefaultEvent event={event} statuses={allStatuses} />;
    },
    month: {
      event: (props: unknown) => {
        const { event } = props as { event: CalendarEvent };
        return <MonthEvent event={event} statuses={allStatuses} />;
      },
    },

    // Week view specific components  
    week: {
      event: (props: unknown) => {
        const { event } = props as { event: CalendarEvent };
        return (
          <WeekEvent
            event={event}
            statuses={allStatuses}
            showAppointmentItemsOnCards={showAppointmentItemsOnCards}
          />
        );
      },
    },

    // Day view specific components
    day: {
      event: (props: unknown) => {
        const { event } = props as { event: CalendarEvent };
        return (
          <DayEvent
            event={event}
            statuses={allStatuses}
            showAppointmentItemsOnCards={showAppointmentItemsOnCards}
          />
        );
      },
    },

  }), [allStatuses, showAppointmentItemsOnCards]);

  const messages = useMemo(() => ({
    allDay: t("allDay"),
    previous: t("previous"),
    next: t("next"),
    today: t("today"),
    month: t("month"),
    week: t("week"),
    day: t("day"),
    agenda: t("agenda"),
    date: t("date"),
    time: t("time"),
    event: t("event"),
    noEventsInRange: t("noEventsInRange"),
    showMore: (total: number) => t("showMore", { count: total }),
    work_week: t("work_week"),
    yesterday: t("yesterday"),
    tomorrow: t("tomorrow"),
    dateFormat: "dd/MM/yyyy",
    dayFormat: "dd/MM",
    dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${format(start, "dd/MM", { locale: ptBR })} - ${format(end, "dd/MM", { locale: ptBR })}`,
    dayHeaderFormat: (date: Date) => format(date, 'EEEE, dd/MM', { locale: ptBR }),
    monthHeaderFormat: (date: Date) => format(date, 'MMMM yyyy', { locale: ptBR }),
    agendaDateFormat: (date: Date) => format(date, 'EEEE, dd/MM/yyyy', { locale: ptBR }),
    agendaTimeFormat: (date: Date) => format(date, 'HH:mm', { locale: ptBR }),
    agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${format(start, 'HH:mm', { locale: ptBR })} - ${format(end, 'HH:mm', { locale: ptBR })}`,
  }), [t]);

  const handleOpenShowMore = () => {
    setIsShowMoreOpen(true);
  };

  // Scroll to current time on component mount and view change
  useEffect(() => {
    if (viewMode === "month") return;
    if (!calendarRef.current) return;

    const isCurrentTimeVisibleDate =
      viewMode === "day"
        ? isSameDay(currentDate, new Date())
        : viewMode === "week";

    if (!isCurrentTimeVisibleDate) return;

    let attempts = 0;
    const maxAttempts = 12;
    let isInterrupted = false;
    let retryTimeoutId: number | null = null;

    const handleManualInterrupt = () => {
      isInterrupted = true;
      cancelScrollRef.current?.();
      cancelScrollRef.current = null;
      if (retryTimeoutId !== null) {
        window.clearTimeout(retryTimeoutId);
      }
    };

    const scrollToCurrentTime = () => {
      if (!calendarRef.current || isInterrupted) return;

      const timeContent = calendarRef.current.querySelector(".rbc-time-content") as HTMLElement | null;
      if (!timeContent) return;

      const indicator = calendarRef.current.querySelector(".rbc-current-time-indicator") as HTMLElement | null;
      if (indicator) {
        const targetScroll = Math.max(0, indicator.offsetTop - 200);
        cancelScrollRef.current?.();
        cancelScrollRef.current = startCancelableSmoothScroll(timeContent, {
          top: targetScroll,
          durationMs: 500,
        });
        return;
      }

      return;
    };

    const tryScroll = () => {
      if (isInterrupted) return;
      attempts += 1;
      scrollToCurrentTime();

      if (!calendarRef.current) return;
      const indicator = calendarRef.current.querySelector(".rbc-current-time-indicator");
      if (!indicator && attempts < maxAttempts) {
        retryTimeoutId = window.setTimeout(tryScroll, 150);
      }
    };

    const timeContent = calendarRef.current.querySelector(".rbc-time-content") as HTMLElement | null;
    window.addEventListener("wheel", handleManualInterrupt, { passive: true, capture: true });
    window.addEventListener("touchmove", handleManualInterrupt, { passive: true, capture: true });
    window.addEventListener("keydown", handleManualInterrupt, { passive: true, capture: true });
    timeContent?.addEventListener("wheel", handleManualInterrupt, { passive: true, capture: true });
    timeContent?.addEventListener("touchmove", handleManualInterrupt, { passive: true, capture: true });

    const timeoutId = window.setTimeout(tryScroll, 100);

    return () => {
      window.clearTimeout(timeoutId);
      if (retryTimeoutId !== null) {
        window.clearTimeout(retryTimeoutId);
      }
      cancelScrollRef.current?.();
      cancelScrollRef.current = null;
      window.removeEventListener("wheel", handleManualInterrupt, { capture: true });
      window.removeEventListener("touchmove", handleManualInterrupt, { capture: true });
      window.removeEventListener("keydown", handleManualInterrupt, { capture: true });
      timeContent?.removeEventListener("wheel", handleManualInterrupt, { capture: true });
      timeContent?.removeEventListener("touchmove", handleManualInterrupt, { capture: true });
    };
  }, [viewMode, currentDate]);


  return (
    <div className="space-y-6">

      {/* React Big Calendar with Drag & Drop */}
      <div ref={calendarRef} className="h-[80vh] md:h-[80vh]">
        <DnDCalendar
          onShowMore={handleOpenShowMore}
          localizer={localizer}
          events={events}
          resources={dayResources}
          tooltipAccessor={() => ""}
          resourceIdAccessor={(resource: object) => (resource as { id?: string }).id ?? ""}
          resourceTitleAccessor={(resource: object) =>
            (resource as { full_name?: string }).full_name ?? "Profissional"
          }
          view={getView()}
          views={["day", "week", "month"]}
          date={currentDate}
          onNavigate={onNavigate}
          onView={(view) => setViewMode(view as "day" | "week" | "month" | "agenda")} // Controlled by parent component
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          onEventDrop={handleEventDrop}
          onEventResize={handleEventResize}
          eventPropGetter={eventStyleGetter}
          components={components}
          messages={messages}
          culture="pt-BR"
          selectable
          resizable
          draggableAccessor={() => true}
          resizableAccessor={() => true}
          min={calendarMin}
          max={new Date(2000, 0, 1, 21, 59, 59)} // 23:59:59
          step={15} // 15-minute intervals
          timeslots={4} // 4 timeslots per hour (15 min each)
          dayLayoutAlgorithm="no-overlap"
          showMultiDayTimes
          popup
          popupOffset={{ x: 10, y: 10 }}
          style={{ height: '100%' }}
          className={cn(
            "rbc-calendar",
            viewMode === "day" ? "rbc-calendar--day-resources" : "",
            viewMode === "week" ? "rbc-calendar--week" : "",
          )}
        />
      </div>
    </div>
  );
}
